import { apiJson } from "../utils/api.js";
import { handleApiError } from "../utils/errorHandler.js";

/**
 * Fetch payment data with date range and pagination
 * @param {string} dateFrom - Start date (YYYY-MM-DD)
 * @param {string} dateTo - End date (YYYY-MM-DD)
 * @param {number} limit - Maximum number of records
 * @param {number} offset - Number of records to skip
 * @returns {Promise<Object>} Payment data
 */
export const fetchPaymentData = async (dateFrom, dateTo, limit, offset) => {
    try {
        const params = {};
        if (dateFrom) params.dateFrom = dateFrom;
        if (dateTo) params.dateTo = dateTo;
        if (limit !== undefined) params.limit = limit;
        if (offset !== undefined) params.offset = offset;

        const response = await apiJson.get(`/kms/order/payment`, { params });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: 3002: Այս պատվերի համար վճարման տեղեկատվություն չի գտնվել: Խնդրում ենք ստուգել պատվերի տվյալները և փորձել կրկին, կամ դիմել աջակցության բաժի:"
        });
        throw error;
    }
};

/**
 * Fetch all payment data for a date range (for export purposes)
 * Handles pagination to fetch all records
 * @param {string} dateFrom - Start date (YYYY-MM-DD)
 * @param {string} dateTo - End date (YYYY-MM-DD)
 * @returns {Promise<Array>} Array of all payment records
 */
export const fetchAllPaymentData = async (dateFrom, dateTo) => {
    try {
        const allPayments = [];
        let offset = 0;
        const limit = 1000; // Large limit to minimize API calls
        let hasMore = true;

        while (hasMore) {
            const response = await fetchPaymentData(dateFrom, dateTo, limit, offset);
            
            // Flatten the response structure
            const flattened = [];
            if (response?.paymentTypeGroups && Array.isArray(response.paymentTypeGroups)) {
                response.paymentTypeGroups.forEach((group) => {
                    const paymentType = group?.paymentType;
                    if (Array.isArray(group?.orderDetails)) {
                        group.orderDetails.forEach((order) => {
                            flattened.push({ ...order, paymentType });
                        });
                    }
                });
            }

            allPayments.push(...flattened);

            // Check if we got fewer records than the limit (last page)
            if (flattened.length < limit) {
                hasMore = false;
            } else {
                offset += limit;
            }
        }

        return allPayments;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Failed to fetch payment data for export. Please try again or contact support."
        });
        throw error;
    }
};

/**
 * Fetch Telegram customer order payment data
 * @param {Object} params - Query parameters (dateFrom, dateTo, limit, offset, companyId, status)
 * @returns {Promise<Object>} Telegram payment data
 */
export const fetchTelegramPaymentData = async (params) => {
    try {
        const response = await apiJson.get(`/kms/order/payment/telegram-customers`, { params });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Failed to fetch Telegram customer payment data."
        });
        throw error;
    }
};

/**
 * Fetch Telegram customer order payment summary
 * @param {Object} params - Query parameters (dateFrom, dateTo, status)
 * @returns {Promise<Array>} Telegram payment summary data
 */
export const fetchTelegramPaymentSummary = async (params) => {
    try {
        const response = await apiJson.get(`/kms/order/payment/telegram-customers/summary`, { params });
        return response.data;
    } catch (error) {
        handleApiError(error, {
            defaultMessage: "Error: Failed to fetch Telegram customer payment summary data."
        });
        throw error;
    }
};
